// src/pages/OurProducts.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import whobanner from "../img/products.jpg";

const OurProducts = () => {
  const SCROLL_SHOW_THRESHOLD = 300;
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);

  const RADIUS = 49;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  const onScroll = useCallback(() => {
    const sc = window.scrollY || window.pageYOffset;
    setShowScroll(sc > SCROLL_SHOW_THRESHOLD);

    const doc = document.documentElement;
    const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
    const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;
    setScrollProgress(Math.min(100, Math.max(0, progress)));

    setIsScrolling(true);

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      scrollTimeoutRef.current = null;
    }, 250);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [onScroll]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToTop();
    }
  };

  // PRODUCT LIST
  const featuredProducts = [
    {
      title: "One Step Medi",
      description: "All-in-One Healthcare Platform",
      imageRight: "/img/onestepimg.jpg",
      link: "/onestepmedi",
    },
    {
      title: "Lauratek",
      description: "Learning Management System",
      imageRight: "/img/lauraimg.jpg",
      link: "/lauratek-lms",
    },
    {
      title: "Shrava360 (HRMS)",
      description: "Streamline human resource operations",
      imageRight: "/img/hrmsimg.jpg",
      link: "/hrms",
    },
    {
      title: "Arogya Narayan (HMS)",
      description: "Smarter Hospitals for Better Care",
      imageRight: "/img/hmsnew.png",
      link: "/hms",
    },
    {
      title: "Vishan (SAMS)",
      description: "Smart School Administration",
      imageRight: "/img/samsimg.jpg",
      link: "/sams",
    },
    {
      title: "HiSphere",
      description: "Intelligent Employee On-boarding Platform",
      imageRight: "/img/hiimg.jpg",
      link: "/hisphare",
    },
    {
      title: "Lauratek v2.0",
      description: "Advanced Digital Learning Platform",
      imageRight: "/img/laura2img.jpg",
      link: "/lms2",
    },
    {
      title: "Log Sphere (KPI)",
      description: "Workplace Device Insights",
      imageRight: "/img/logimg.jpg",
      link: "/kpi",
    },
    {
      title: "DevTalent",
      description: "Skill Evaluation Platform",
      imageRight: "/img/devimg.jpg",
      link: "/devtalnet",
    },
    {
      title: "ROB N GRAB",
      description: "Smart Recruitment & Onboarding Platform",
      imageRight: "/img/robimg.jpg",
      link: "/Rob",
    },
  ];

  // PRODUCT CARD COMPONENT
  const ProductCard = ({ product }) => {
    return (
      <Link
        to={product.link}
        className="relative group rounded-2xl p-6 bg-white/10 backdrop-blur-md 
        shadow-xl border border-white/20 hover:bg-white/20 transition-all duration-300
        flex flex-col items-center justify-between overflow-hidden"
      >
        <div className="w-full h-40 overflow-hidden rounded-xl mb-4">
          <img
            src={product.imageRight}
            alt={product.title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-all duration-500"
          />
        </div>

        <h3 className="text-xl font-bold text-gray-900 text-center mb-1">
          {product.title}
        </h3>

        <p className="text-gray-700 text-sm text-center mb-4">
          {product.description}
        </p>

        <button
          className="px-5 py-2 rounded-full text-white font-semibold bg-gradient-to-r 
          from-[#210A4A] to-[#3E66F3] shadow-md hover:shadow-lg hover:scale-105 
          transition-all duration-300"
        >
          Explore Now
        </button>

        <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent 
        opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-500"></div>
      </Link>
    );
  };

  const dashOffset = CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;

  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>Our Products | SecurXpert Technologies - Hyderabad</title>
      </Helmet>

      <Navbar />

      {/* HERO BANNER */}
      <header className="relative w-full">
        <div
          className="relative w-full h-[317px] bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: `url(${whobanner})` }}
        >
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 z-10">
            <h1 className="text-4xl font-bold mb-4">Our Products</h1>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">Home</Link>
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-[2px]"
              >
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>Our Products</span>
            </div>
          </div>
        </div>
      </header>

      {/* PRODUCT GRID */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">

            {featuredProducts.map((product, index) => {

              // ⭐ Center ROB N GRAB card in its own row
              if (product.title === "ROB N GRAB") {
                return (
                  <div
                    key={index}
                    className="col-span-1 sm:col-span-2 lg:col-span-3 flex justify-center"
                  >
                    <div className="w-full max-w-[380px]">
                      <ProductCard product={product} />
                    </div>
                  </div>
                );
              }

              // Normal cards
              return <ProductCard key={index} product={product} />;
            })}

          </div>
        </div>
      </section>

      <Footer />

      {/* SCROLL TO TOP BUTTON */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer 
          flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r 
          from-blue-500 to-blue-700 shadow-xl hover:scale-110 transition-all"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>

          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}

    </div>
  );
};

export default OurProducts;
